import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Link2, Unlink, RefreshCw, CheckCircle2, CircleAlert } from "lucide-react";
import { toast } from "sonner";
import { PortalPage, Card, Btn, Spinner, ErrorNote } from "@/components/PortalPage";
import { api, getToken } from "@/lib/api";
import { fmtDate } from "@/lib/lso";

export default function QuickBooks() {
  const [params] = useSearchParams();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(params.get("error"));
  const load = useCallback(() => api("/quickbooks/status").then(setS).catch((e) => setErr(e.message)), []);
  useEffect(() => { load(); if (params.get("connected")) toast.success("QuickBooks connected."); }, [load, params]);

  const connect = () => { window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/quickbooks/oauth/start?jwt=${encodeURIComponent(getToken() || "")}`; };
  const disconnect = async () => {
    if (!window.confirm("Disconnect QuickBooks? Invoices for store orders and rental courses will stop until you reconnect.")) return;
    setBusy("disc");
    try { await api("/quickbooks/oauth/disconnect-me", { method: "POST" }); await load(); } catch (e) { setErr(e.message); } finally { setBusy(null); }
  };
  const sync = async () => {
    setBusy("sync"); setMsg(null);
    try { const r = await api("/quickbooks/invoices/sync-mine", { method: "POST" }); setMsg(`Checked ${r.checked} invoice(s) · ${r.newly_paid} newly paid.${r.errors?.length ? ` ${r.errors.length} error(s).` : ""}`); }
    catch (e) { setErr(e.message); } finally { setBusy(null); }
  };

  return (
    <PortalPage eyebrow="PAYMENTS" title="QuickBooks" testId="quickbooks-page">
      <ErrorNote>{err}</ErrorNote>
      {!s && !err && <Spinner />}
      {s && (
        <Card style={{ borderColor: s.connected ? "var(--accent)" : "#333" }} data-testid="qbo-status">
          {s.connected ? <CheckCircle2 size={30} className="text-[var(--accent)]" /> : <CircleAlert size={30} className="text-[#F5A623]" />}
          <p className="mt-2 font-display text-2xl font-bold uppercase" data-testid="qbo-status-label">{s.connected ? "Connected" : s.configured ? "Not connected" : "QuickBooks keys missing"}</p>
          <div className="mt-2 space-y-1 text-sm text-neutral-400">
            <p>Environment: <span className="text-white">{s.environment}</span></p>
            {s.realm_id && <p>Company ID: <span className="font-mono text-white">{s.realm_id}</span></p>}
            {s.connected_at && <p>Connected: {fmtDate(s.connected_at)}</p>}
          </div>
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {s.connected ? (
              <>
                <Btn busy={busy === "sync"} onClick={sync} data-testid="qbo-sync"><RefreshCw size={16} /> Check invoice payments</Btn>
                <Btn variant="outline" busy={busy === "disc"} onClick={disconnect} data-testid="qbo-disconnect"><Unlink size={16} /> Disconnect</Btn>
              </>
            ) : <Btn disabled={!s.configured} onClick={connect} data-testid="qbo-connect"><Link2 size={16} /> Connect to QuickBooks</Btn>}
          </div>
          {msg && <p className="mt-3 text-sm text-neutral-300" data-testid="qbo-sync-msg">{msg}</p>}
        </Card>
      )}
    </PortalPage>
  );
}
